import { ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, OnInit, ViewEncapsulation, effect, inject, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';

import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SelectModel } from 'src/tidy-ui-components/components/form/select/select.model';
import { SyncStatusDotComponent, SyncDotInfo } from 'src/shared/components/sync-status-dot/sync-status-dot.component';

import { PlatformPillsComponent } from './platform-pills.component';
import { SEASONAL_POLICIES, SeasonalPolicy, SeasonalWindowState, toIsoDate, validateSeasonalWindows } from './booking-settings.model';

interface SeasonalWindow {
  id: string;
  // One FormGroup per window: { name, startDate, endDate, policy }.
  form: FormGroup;
}

/** VRBO's own tier names, title-cased for the select. */
const POLICY_LABELS: Record<SeasonalPolicy, string> = {
  relaxed: 'Relaxed',
  moderate: 'Moderate',
  firm: 'Firm',
  strict: 'Strict',
  non_refundable: 'Non-refundable',
};

@Component({
  selector: 'seasonal-cancellation-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TidyUiComponentsModule, SyncStatusDotComponent, PlatformPillsComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <div class="sc-card">
      <div class="sc__head">
        <div class="sc__head-text">
          <span class="sc__title-line">
            <tidy-text [body]="'Seasonal cancellation'" class="sc__title bold" />
            <sync-status-dot [sync]="sync()" />
            <platform-pills [key]="'seasonal_cancellation'" />
          </span>
          <tidy-text
            [helper]="'Apply a stricter Vrbo cancellation policy during specific date windows — peak season, holidays, big events.'"
            class="sc__sub" />
        </div>
        <tidy-text
          class="white-link sc__add"
          [actionBody]="'+ Add Window'"
          (action)="addWindow()" />
      </div>

      <div class="sc-window-list">
        @for (w of windows; track w.id) {
          <div class="sc-window">
            <div [formGroup]="w.form" class="sc-window__main">
              <div class="sc-window__fields">
                <tidy-input
                  [label]="'Window Name'"
                  [form]="w.form.controls.name"
                  formControlName="name"
                  class="sc-window__name-input" />
                <tidy-datepicker
                  [label]="'Start'"
                  [form]="w.form.controls.startDate"
                  formControlName="startDate" />
                <tidy-datepicker
                  [label]="'End'"
                  [form]="w.form.controls.endDate"
                  formControlName="endDate" />
                <tidy-select
                  [label]="'Policy'"
                  [items]="policyItems"
                  [form]="w.form.controls.policy"
                  formControlName="policy" />
              </div>
              @if (windowErrorById[w.id]; as windowError) {
                <tidy-text [helper]="windowError" class="sc-error" />
              }
            </div>
            <button type="button" class="sc-window__remove" aria-label="Remove window" (click)="removeWindow(w)">
              <span class="sc-window__remove-icon" aria-hidden="true"></span>
            </button>
          </div>
        }
        @if (windows.length === 0) {
          <tidy-text [helper]="'No seasonal windows. Your default cancellation policy applies year-round.'" class="sc-empty" />
        }
      </div>
    </div>
  `,
  styles: [`
    /* Matches the Channel card's sub-card treatment (see .rt-section). */
    .sc-card {
      border: 1px solid var(--tidy-border-light);
      border-radius: 10px;
      padding: 14px 16px;
      background: var(--tidy-bg-card);
      display: block;
      color: var(--tidy-text-primary);
    }
    .sc__head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 12px;
    }
    .sc__title-line { display: inline-flex; align-items: center; gap: 6px; }
    .sc__title .body-text { font-size: 14px; font-weight: 700; color: var(--tidy-text-primary); }
    .sc__sub .helper-text { font-size: 12px; color: var(--tidy-text-light); line-height: 1.5; max-width: 760px; }
    .sc__add { white-space: nowrap; flex-shrink: 0; }

    .sc-window-list { display: flex; flex-direction: column; gap: 10px; }
    .sc-window { display: flex; align-items: flex-start; gap: 8px; }
    .sc-window__main { flex: 1; min-width: 0; }
    .sc-window__fields {
      display: grid;
      grid-template-columns: minmax(140px, 1.4fr) 1fr 1fr minmax(140px, 1fr);
      gap: 10px;
      align-items: end;
    }
    @media (max-width: 720px) {
      .sc-window__fields { grid-template-columns: 1fr 1fr; }
    }
    .sc-window__remove {
      background: none;
      border: none;
      padding: 6px;
      margin-top: 22px;
      cursor: pointer;
      line-height: 0;
    }
    .sc-window__remove-icon {
      display: inline-block;
      width: 10px;
      height: 10px;
      position: relative;
    }
    .sc-window__remove-icon::before,
    .sc-window__remove-icon::after {
      content: '';
      position: absolute;
      inset: 4px -1px;
      background: var(--tidy-text-light);
    }
    .sc-window__remove-icon::before { transform: rotate(45deg); }
    .sc-window__remove-icon::after { transform: rotate(-45deg); }

    .sc-error {
      display: block;
      margin-top: 8px;
      .helper-inline { font-size: 12px; color: var(--red) !important; }
    }
    .sc-empty .helper-text { font-size: 12px; color: var(--tidy-text-light); }
  `],
})
export class SeasonalCancellationComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  /** Controlled value from the parent. Null in demo mode → keep the local sample. */
  value = input<SeasonalWindowState[] | null>(null);
  valueChange = output<SeasonalWindowState[]>();

  sync = input<SyncDotInfo | null>(null);

  // True while applying an incoming `value` so the resulting form writes don't
  // echo back out as `valueChange` (which would trigger a spurious save).
  private applying = false;

  // tidy-select shows the picked value untranslated, so translate up front.
  readonly policyItems: SelectModel[] = SEASONAL_POLICIES.map(policy => ({
    value: policy, viewValue: new TranslationPipe().transform(POLICY_LABELS[policy]),
  }));

  windowErrorById: Record<string, string> = {};

  // The windows as loaded/last saved — a window still identical to one of these skips
  // the past-start rule (see validateSeasonalWindows).
  private kept: SeasonalWindowState[] = [];

  /** Monotonic suffix for generated window ids (see addWindow). */
  private windowSeq = 0;

  // Demo-mode sample; real mode replaces it the moment `value` arrives.
  windows: SeasonalWindow[] = [
    {
      id: 'sw1',
      form: this.fb.group({
        name: ['Holiday season'],
        startDate: ['2027-12-15'],
        endDate: ['2028-01-05'],
        policy: ['non_refundable'],
      }),
    },
  ];

  constructor() {
    effect(() => {
      const v = this.value();
      if (v) { this.applyState(v); }
    });
  }

  ngOnInit(): void {
    for (const w of this.windows) { this.subscribeWindow(w); }
    this.refreshErrors(this.getValue());
  }

  addWindow(): void {
    const id = `sw-${Date.now()}-${this.windowSeq++}`;
    const win: SeasonalWindow = {
      id,
      form: this.fb.group({ name: [''], startDate: [''], endDate: [''], policy: [''] }),
    };
    this.subscribeWindow(win);
    this.windows = [...this.windows, win];
    this.emitChange();
  }

  removeWindow(w: SeasonalWindow): void {
    this.windows = this.windows.filter(x => x.id !== w.id);
    this.emitChange();
  }

  private subscribeWindow(w: SeasonalWindow): void {
    w.form.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.emitChange());
  }

  private applyState(v: SeasonalWindowState[]): void {
    this.applying = true;
    this.kept = v;
    try {
      this.windows = v.map(w => {
        const win: SeasonalWindow = {
          id: w.id,
          form: this.fb.group({
            name: [w.name],
            startDate: [w.startDate],
            endDate: [w.endDate],
            policy: [w.policy],
          }),
        };
        this.subscribeWindow(win);
        return win;
      });
      this.refreshErrors(v);
      this.cdr.markForCheck();
    } finally {
      this.applying = false;
    }
  }

  private getValue(): SeasonalWindowState[] {
    return this.windows.map(w => {
      const raw = w.form.getRawValue();
      return {
        id: w.id,
        name: typeof raw.name === 'string' ? raw.name : '',
        startDate: toIsoDate(raw.startDate),
        endDate: toIsoDate(raw.endDate),
        policy: raw.policy || '',
      };
    });
  }

  private refreshErrors(v: SeasonalWindowState[]): void {
    this.windowErrorById = Object.fromEntries(validateSeasonalWindows(v, this.kept).map(e => [e.id, e.message]));
  }

  private emitChange(): void {
    if (this.applying) { return; }
    const v = this.getValue();
    this.refreshErrors(v);
    this.cdr.markForCheck();
    this.valueChange.emit(v);
  }
}
